import React, { useState } from "react";
import {
  Button,
  Container,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useCreateFranchiseMutation } from "generated/graphql";

/**
 * FranchiseCreate
 *
 * Admin page to create a new franchise.
 * Uses CreateFranchise mutation which only requires a name.
 * GM/AGM and teams can be assigned after creation via FranchiseEdit.
 */
export default function FranchiseCreate() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Mutation to create franchise
  const [createFranchise, { loading: creating }] = useCreateFranchiseMutation();

  // Form state
  const [name, setName] = useState("");

  async function handleCreate() {
    if (!name.trim()) return;

    try {
      const result = await createFranchise({
        variables: {
          name: name.trim(),
        },
      });

      if (result.data?.createFranchise) {
        notifications.show({
          title: "Success",
          message: `Franchise "${result.data.createFranchise.name}" created successfully`,
          color: "green",
        });
        // Navigate to edit page to set GM/AGM and other details
        navigate(`/admin-hub/franchises/${result.data.createFranchise.id}/edit`);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to create franchise",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "An error occurred while creating the franchise",
        color: "red",
      });
    }
  }

  return (
    <Container size="xl" px="lg" py="lg">
      <Stack gap="md">
        <Group justify="flex-start">
          <Button
            variant="filled"
            color="blue"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin-hub/franchises")}
            size="sm"
          >
            Back to Franchises
          </Button>
        </Group>

        <div className="space-y-4 max-w-2xl">
          <Title c={isDark ? "white" : "black"}>Create Franchise</Title>
          <Text c={isDark ? "gray.4" : "gray.7"}>
            Create a new franchise. After creation, you can assign GM/AGM and manage teams.
          </Text>

          <div className="space-y-4">
            <TextInput
              label="Franchise Name"
              placeholder="Enter franchise name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              withAsterisk
              autoComplete="off"
              autoFocus
            />

            <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
              Note: Franchises are created as active by default. You can assign a GM, AGM, and teams after creation.
            </Text>

            <Group mt="md">
              <Button
                className="bg-blue-500 hover:bg-blue-600"
                onClick={handleCreate}
                disabled={!name.trim() || creating}
                loading={creating}
              >
                Create Franchise
              </Button>
              <Button variant="light" color="gray" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </Group>
          </div>
        </div>
      </Stack>
    </Container>
  );
}
